import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './B2BSales.module.css';
import Header from '../../Header';
import { useNavigate } from 'react-router-dom';
import { local_signage, local_url } from '../../Const';

export const B2BSales = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedPricingScheme, setSelectedPricingScheme] = useState('HORECA');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Date validation state
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  
  // NEW: Add State for Segments, Areas, Branches, and BranchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  
  // State for table
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Session timeout dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // FIXED: Helper functions to handle date/time without UTC conversion
  const parseLocalDateTime = (dateString) => {
    if (!dateString) return new Date();
    
    try {
      // Remove the 'Z' and treat as local time (IST) instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      return isNaN(date.getTime()) ? new Date() : date;
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return new Date();
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDateTime(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date only:', dateString, error);
      return '';
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDateTime(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time only:', dateString, error);
      return '';
    }
  };

  // Calculate TAT (Turn Around Time) in hours and minutes
  const calculateTAT = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return 'N/A';
    
    try {
      const start = parseLocalDateTime(startDateTime);
      const end = parseLocalDateTime(endDateTime);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
      
      const diffMs = end.getTime() - start.getTime();
      
      if (diffMs < 0) return 'N/A'; // End time before start time
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours === 0) {
        return `${diffMinutes}m`;
      } else {
        return `${diffHours}h ${diffMinutes}m`;
      }
    } catch (error) {
      console.error('Error calculating TAT:', startDateTime, endDateTime, error);
      return 'N/A';
    }
  };

  // Function to clear all localStorage and navigate to login
  const handleSessionTimeout = () => {
    localStorage.clear();
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    navigate('/Login', { replace: true });
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  // Session Timeout Dialog Component
  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Date validation function
  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return true; // Allow empty dates
    
    const daysDiff = endDate.diff(startDate, 'day');
    return daysDiff >= 0 && daysDiff <= 45; // Maximum 45 days range
  };

  // Update date range validation when dates change
  useEffect(() => {
    const isValid = validateDateRange(fromDate, toDate);
    setIsDateRangeValid(isValid);
  }, [fromDate, toDate]);

  // Date picker styling with conditional red border
  const getDatePickerStyle = (isValid = true) => ({
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: isValid ? '#dee2e6' : '#f44336', // Red border when invalid
      },
      '&:hover fieldset': {
        borderColor: isValid ? '#adb5bd' : '#f44336',
      },
      '&.Mui-focused fieldset': {
        borderColor: isValid ? '#667eea' : '#f44336',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: isValid ? '#6c757d' : '#f44336', // Red label when invalid
    },
  });

  // Legacy date picker style for compatibility
  const datePickerStyle = getDatePickerStyle();

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '40%',
            height: '40%',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading System Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we load indent demand data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              ${theme.palette.primary.main}, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  const pricingSchemeOptions = [
    { value: 'ALL', label: 'ALL' },
    { value: 'HORECA', label: 'HORECA' },
    { value: 'RETAIL', label: 'RETAIL' },
    { value: 'WHOLESALE', label: 'WHOLESALE' },
    { value: 'HOTEL', label: 'HOTEL' }
  ];

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching business segments:', error);
        }
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      setLoadingAreas(true);
      try {
        const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          // Add ALL option and set the areas
          const areasWithAll = [
            // { SFAreaId: 'ALL', SFAreaName: 'ALL' },
            ...data
          ];
          setAreas(areasWithAll);
          setSelectedAreaObj(null);
          setBranches([]); // Clear branches when areas change
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching areas by segment:', error);
        }
        setAreas([]);
        setBranches([]);
        setSelectedAreaObj(null);
        setSelectedBranchObj(null);
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      setLoadingBranches(true);
      try {
        // Use SFAreaId from the API response structure
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await apiCall(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching branch list:', error);
        }
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  // API Functions with 401 handling
  const fetchAreas = async () => {
    try {
      const response = await apiCall(`${local_url}/api/SFArea/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const areasWithAll = [
          { sfAreaId: 'ALL', sfAreaName: 'ALL' },
          ...data
        ];
        setAreas(areasWithAll);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching areas:', error);
      }
    }
  };

  const fetchBranches = async (sfAreaId) => {
    try {
      const response = await apiCall(`${local_url}/api/Company/GetBranchList/${sfAreaId}/SEGMENT/ALL`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const branchesWithAll = [
          { branchID: 'ALL', branchName: 'ALL' },
          ...data
        ];
        setBranches(branchesWithAll);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching branches:', error);
      }
    }
  };

  // Fetch B2B sales report data
  const fetchB2BSalesData = async (areaId, branchId, fromDate, toDate, customerId = 'ALL', salesChannel = 'ALL', pricingSchemeId = 'HORECA', startRecord = 1, rowsCount = 500) => {
    try {
      const startDate = fromDate ? fromDate.format('YYYYMMDD') : dayjs().subtract(1, 'month').format('YYYYMMDD');
      const endDate = toDate ? toDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');

      const response = await apiCall(
        `${local_signage}/api/v1/b2bsalereport?AreaID=00001&CustomerID=${customerId}&SalesChannel=${salesChannel}&PricingSchemeId=${pricingSchemeId}&BranchId=${branchId}&StartDate=${startDate}&EndDate=${endDate}&StartRecord=${startRecord}&RowsCount=${rowsCount}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        return {
          data: transformApiData(result.data || []),
          count: result.count && result.count[0] ? result.count[0].cnt : 0
        };
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch B2B sales data:', response.status, errorText);
        alert(`Failed to fetch data: ${response.status} - ${errorText}`);
        return { data: [], count: 0 };
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching B2B sales data:', error);
        alert('Error fetching data. Please check your connection and try again.');
      }
      return { data: [], count: 0 };
    }
  };

  // Transform B2B Sales API data
  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) {
      console.error('API data is not an array:', apiData);
      return [];
    }

    return apiData.map((item, index) => {
      try {
        const startRecord = (currentPage - 1) * rowsPerPage + 1;
        return {
          id: index + 1,
          sno: startRecord + index,
          branchId: item.BranchId || '',
          saleid: item.saleid || '',
          businessDate: formatDateOnly(item.businessDate),
          saleOrderId: item.SaleOrderId || '',
          saleType: item.SaleType || '',
          orderDate: formatDateOnly(item.OrderDate),
          channelId: item.ChannelID || '',
          customerName: item.CustomerName || '',
          mobile: item.mobile || '',
          total: Number(item.Total) || 0,
          paymentMethod: item.PaymentMethod || '',
          branchName: item.BranchName || '',
          totalWeight: Number(item.TotalWeight) || 0,
          isReturnedSale: item.IsReturnedSale || '',
          pricingSchemeId: item.PricingSchemeId || ''
        };
      } catch (error) {
        console.error('Error transforming item:', item, error);
        const startRecord = (currentPage - 1) * rowsPerPage + 1;
        return {
          id: index + 1,
          sno: startRecord + index,
          branchId: item.BranchId || 'N/A',
          saleid: item.saleid || 'N/A',
          businessDate: 'N/A',
          saleOrderId: item.SaleOrderId || 'N/A',
          saleType: item.SaleType || 'N/A',
          orderDate: 'N/A',
          channelId: item.ChannelID || 'N/A',
          customerName: item.CustomerName || 'N/A',
          mobile: item.mobile || 'N/A',
          total: 0,
          paymentMethod: item.PaymentMethod || 'N/A',
          branchName: item.BranchName || 'N/A',
          totalWeight: 0,
          isReturnedSale: item.IsReturnedSale || 'N/A',
          pricingSchemeId: item.PricingSchemeId || 'N/A'
        };
      }
    });
  };

  // Load areas on component mount - COMMENTED OUT as it's replaced by segment-based loading
  // useEffect(() => {
  //   if (userId && authToken) {
  //     fetchAreas();
  //   }
  // }, [userId, authToken]);

  // Load branches when area changes - COMMENTED OUT as it's replaced by segment-based loading
  // useEffect(() => {
  //   if (selectedAreaObj && selectedAreaObj.sfAreaId !== 'ALL') {
  //     fetchBranches(selectedAreaObj.sfAreaId);
  //     setSelectedBranchObj(null);
  //   } else {
  //     setBranches([]);
  //     setSelectedBranchObj(null);
  //   }
  // }, [selectedAreaObj]);

  // Initialize data
  useEffect(() => {
    setRawData([]);
    setFilteredData([]);
  }, []);

  // Apply filters to fetch B2B sales data
  const applyFilters = async () => {
    if (!selectedAreaObj) {
      alert('Please select an Area to fetch data.');
      return;
    }

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be after To Date.');
      return;
    }

    if (!isDateRangeValid) {
      alert('Date range cannot exceed 45 days. Please select a smaller date range.');
      return;
    }

    setLoading(true);
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);
    setHasSubmitted(true); // Enable export button

    try {
      const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
      const startRecord = (currentPage - 1) * rowsPerPage + 1;

      console.log('Fetching B2B sales data with params:', {
        areaId,
        branchId,
        fromDate: fromDate.format('YYYYMMDD'),
        toDate: toDate.format('YYYYMMDD'),
        pricingScheme: selectedPricingScheme,
        startRecord,
        rowsCount: rowsPerPage
      });

      const result = await fetchB2BSalesData(
        areaId,
        branchId,
        fromDate,
        toDate,
        'ALL',
        'ALL',
        selectedPricingScheme,
        startRecord,
        rowsPerPage
      );

      console.log('Received B2B sales data:', result);

      setRawData(result.data);
      setFilteredData(result.data);
      setTotalCount(result.count);

      if (result.data.length === 0) {
        alert('No data found for the selected criteria.');
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error applying filters:', error);
        alert('Error fetching data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedArea('');
    setSelectedBranch('');
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setBranchType(''); // NEW: Clear segment selection
    setFromDate(dayjs().subtract(1, 'month'));
    setToDate(dayjs());
    setSelectedPricingScheme('HORECA');
    setSearchTerm('');
    setRawData([]);
    setFilteredData([]);
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setHasSubmitted(false); // Disable export button
  };

  // Export function for B2B sales data - exports ALL data
  const exportToXLSX = async () => {
    if (!hasSubmitted) {
      alert('Please submit the form first to enable export.');
      return;
    }

    if (totalCount === 0) {
      alert('No data to export.');
      return;
    }

    setLoading(true);
    try {
      // Fetch all data for export
      const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
      
      const result = await fetchB2BSalesData(
        areaId,
        branchId,
        fromDate,
        toDate,
        'ALL',
        'ALL',
        selectedPricingScheme,
        1, // Start from first record
        totalCount // Get all records
      );

      if (result.data.length === 0) {
        alert('No data to export.');
        return;
      }

      const exportData = result.data.map((row, index) => ({
        'S.No': index + 1,
        'Branch ID': row.branchId,
        'Sale ID': row.saleid,
        'Business Date': row.businessDate,
        'Sale Order ID': row.saleOrderId,
        'Sale Type': row.saleType,
        'Order Date': row.orderDate,
        'Channel ID': row.channelId,
        'Customer Name': row.customerName,
        'Mobile': row.mobile,
        'Total': row.total,
        'Payment Method': row.paymentMethod,
        'Branch Name': row.branchName,
        'Total Weight': row.totalWeight,
        'Returned Sale': row.isReturnedSale,
        'Pricing Scheme': row.pricingSchemeId
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'B2B Sales Report');
      XLSX.writeFile(wb, `b2b_sales_report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      
      alert(`Successfully exported ${exportData.length} records.`);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Search and sort B2B sales data
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase().trim();
      data = data.filter(item =>
        (item.customerName && item.customerName.toString().toLowerCase().includes(lower)) ||
        (item.saleid && item.saleid.toString().toLowerCase().includes(lower)) ||
        (item.mobile && item.mobile.toString().includes(lower)) ||
        (item.branchName && item.branchName.toString().toLowerCase().includes(lower)) ||
        (item.branchId && item.branchId.toString().toLowerCase().includes(lower)) ||
        (item.paymentMethod && item.paymentMethod.toString().toLowerCase().includes(lower))
      );
    }

    const startRecord = (currentPage - 1) * rowsPerPage + 1;

    if (!sortConfig.key) {
      return data.map((item, index) => ({
        ...item,
        sno: startRecord + index
      }));
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }).map((item, index) => ({
      ...item,
      sno: startRecord + index
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Auto-redirect to first page when search results change
  useEffect(() => {
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination - Handle API pagination
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const paginatedData = filteredAndSearchedData; // Data is already paginated from API

  // Handle page change and fetch new data
  const handlePageChange = async (newPage) => {
    if (newPage === currentPage) return;
    
    setCurrentPage(newPage);
    
    if (selectedAreaObj) {
      setLoading(true);
      try {
        const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
        const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
        const startRecord = (newPage - 1) * rowsPerPage + 1;

        const result = await fetchB2BSalesData(
          areaId,
          branchId,
          fromDate,
          toDate,
          'ALL',
          'ALL',
          selectedPricingScheme,
          startRecord,
          rowsPerPage
        );

        setRawData(result.data);
        setFilteredData(result.data);
        setTotalCount(result.count);
      } catch (error) {
        console.error('Error fetching page data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Header/>
      
      <div className={styles.indent_dashboard}>
        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />

        {/* Filters Section */}
        <div className={styles.indent_filters_section}>
          <div className={styles.indent_filters_grid}>
            {/* NEW: Segment Dropdown */}
            <div className={styles.indent_filter_group}>
              <select
                value={branchType}
                onChange={e => setBranchType(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Segment</option>
                {segments.map(segment => (
                  <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* UPDATED: Area Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => {
                  setSelectedAreaObj(newValue);
                }}
                disabled={!branchType || loadingAreas} // Disabled until segment is selected or while loading
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* UPDATED: Branch Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.branchName || ''}
                value={selectedBranchObj}
                onChange={(event, newValue) => {
                  setSelectedBranchObj(newValue);
                }}
                disabled={!selectedAreaObj || loadingBranches} // Disabled until area is selected or while loading
                loading={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingBranches ? "Loading Branches..." : "Select Branch"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={getDatePickerStyle(isDateRangeValid)}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
              {!isDateRangeValid && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#f44336', 
                  marginTop: '4px',
                  fontWeight: '500'
                }}>
                  Date range cannot exceed 45 days
                </div>
              )}
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={getDatePickerStyle(isDateRangeValid)}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
              {!isDateRangeValid && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#f44336', 
                  marginTop: '4px',
                  fontWeight: '500'
                }}>
                  {fromDate && toDate ? 
                    `Range: ${toDate.diff(fromDate, 'day')} days (max 45 days allowed)` :
                    'Date range cannot exceed 45 days'
                  }
                </div>
              )}
            </div>

            <div className={styles.indent_filter_group}>
              <select 
                value={selectedPricingScheme}
                onChange={(e) => setSelectedPricingScheme(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Pricing Scheme</option>
                {pricingSchemeOptions.map(scheme => (
                  <option key={scheme.value} value={scheme.value}>{scheme.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.indent_filter_actions}>
            <button 
              onClick={applyFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_primary}`} 
              disabled={loading || !isDateRangeValid}
              style={{
                opacity: (loading || !isDateRangeValid) ? 0.6 : 1,
                cursor: (loading || !isDateRangeValid) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button 
              onClick={clearFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}
            >
              Clear
            </button>
            <button 
              onClick={exportToXLSX} 
              className={`${styles.indent_btn} ${styles.indent_btn_success}`}
              disabled={!hasSubmitted || loading || !isDateRangeValid}
              style={{
                opacity: (!hasSubmitted || loading || !isDateRangeValid) ? 0.6 : 1,
                cursor: (!hasSubmitted || loading || !isDateRangeValid) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Exporting...' : 'Export XLSX'}
            </button>
            <div className={styles.indent_search_group}>
              <input
                type="text"
                placeholder="Search by Customer Name, Sale ID, Mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.indent_search_input}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={styles.indent_results_summary}>
          <span>
            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} total records
          </span>
          <div className={styles.indent_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
                // Re-apply filters with new page size
                if (selectedAreaObj) {
                  applyFilters();
                }
              }}
              className={styles.indent_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section */}
        <div className={styles.indent_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.indent_no_data}>
              <p>No data found. Please select a Segment and Area, then click Apply to fetch data.</p>
            </div>
          ) : (
            <div className={styles.indent_table_container}>
              <table className={styles.indent_data_table}>
                <thead>
                  <tr>
                    <th className={styles.indent_non_sortable}>
                      S.No
                    </th>
                    <th onClick={() => handleSort('branchId')} className={styles.indent_sortable}>
                      Branch ID {sortConfig.key === 'branchId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('saleid')} className={styles.indent_sortable}>
                      Sale ID {sortConfig.key === 'saleid' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('businessDate')} className={styles.indent_sortable}>
                      Business Date {sortConfig.key === 'businessDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('saleOrderId')} className={styles.indent_sortable}>
                      Sale Order ID {sortConfig.key === 'saleOrderId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('saleType')} className={styles.indent_sortable}>
                      Sale Type {sortConfig.key === 'saleType' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('orderDate')} className={styles.indent_sortable}>
                      Order Date {sortConfig.key === 'orderDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('channelId')} className={styles.indent_sortable}>
                      Channel ID {sortConfig.key === 'channelId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('customerName')} className={styles.indent_sortable}>
                      Customer Name {sortConfig.key === 'customerName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('mobile')} className={styles.indent_sortable}>
                      Mobile {sortConfig.key === 'mobile' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('total')} className={styles.indent_sortable}>
                      Total {sortConfig.key === 'total' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('paymentMethod')} className={styles.indent_sortable}>
                      Payment Method {sortConfig.key === 'paymentMethod' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('branchName')} className={styles.indent_sortable}>
                      Branch Name {sortConfig.key === 'branchName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('totalWeight')} className={styles.indent_sortable}>
                      Total Weight {sortConfig.key === 'totalWeight' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('isReturnedSale')} className={styles.indent_sortable}>
                      Returned Sale {sortConfig.key === 'isReturnedSale' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('pricingSchemeId')} className={styles.indent_sortable}>
                      Pricing Scheme {sortConfig.key === 'pricingSchemeId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.sno}</td>
                      <td className={styles.indent_ticket_id}>{row.branchId}</td>
                      <td className={styles.indent_ticket_id}>{row.saleid}</td>
                      <td>{row.businessDate}</td>
                      <td>{row.saleOrderId}</td>
                      <td>{row.saleType}</td>
                      <td>{row.orderDate}</td>
                      <td>{row.channelId}</td>
                      <td>{row.customerName}</td>
                      <td>{row.mobile}</td>
                      <td className={styles.indent_amount}>₹{Number(row.total).toLocaleString('en-IN')}</td>
                      <td>{row.paymentMethod}</td>
                      <td>{row.branchName}</td>
                      <td className={styles.indent_amount}>{row.totalWeight}</td>
                      <td>{row.isReturnedSale}</td>
                      <td>{row.pricingSchemeId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.indent_pagination}>
            {/* First Page Button */}
            <button 
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div className={styles.indent_page_numbers}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default B2BSales;